export interface ApplicationOriginEnvironment {
  readonly VITE_APP_BASE_URL?: string;
  readonly VITE_APP_PLATFORM_URL?: string;
}

export interface ApplicationOrigins {
  readonly app: URL;
  readonly platform: URL;
}

export class ApplicationOriginConfigurationError extends Error {
  readonly reasonCode: string;

  constructor(reasonCode: string) {
    super('Адреса приложений настроены некорректно.');
    this.name = 'ApplicationOriginConfigurationError';
    this.reasonCode = reasonCode;
  }
}

const isLocalHttpHost = (hostname: string): boolean =>
  hostname === 'localhost' ||
  hostname.endsWith('.localhost') ||
  hostname.endsWith('.loc') ||
  hostname.endsWith('.local');

const parseOrigin = (value: string | undefined, kind: 'app' | 'platform'): URL => {
  if (!value) {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_missing`);
  }

  let origin: URL;

  try {
    origin = new URL(value);
  } catch {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_invalid`);
  }

  if (origin.protocol !== 'https:' && origin.protocol !== 'http:') {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_scheme_invalid`);
  }

  if (origin.username || origin.password) {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_credentials_forbidden`);
  }

  if (origin.pathname !== '/' || origin.search || origin.hash) {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_not_canonical`);
  }

  if (origin.protocol === 'http:' && !isLocalHttpHost(origin.hostname)) {
    throw new ApplicationOriginConfigurationError(`${kind}_origin_insecure`);
  }

  return new URL(origin.origin);
};

export const resolveApplicationOrigins = (
  environment: ApplicationOriginEnvironment = import.meta.env,
): ApplicationOrigins => {
  const app = parseOrigin(environment.VITE_APP_BASE_URL, 'app');
  const platform = parseOrigin(environment.VITE_APP_PLATFORM_URL, 'platform');

  if (app.origin === platform.origin) {
    throw new ApplicationOriginConfigurationError('application_origins_not_distinct');
  }

  return { app, platform };
};

export const resolveAppImpersonationHandoffUrl = (
  organizationId: string,
  environment?: ApplicationOriginEnvironment,
): URL => {
  const destination = new URL('/auth/impersonation-handoff', resolveApplicationOrigins(environment).app);
  destination.hash = organizationId;

  return destination;
};

export const resolvePlatformHomeUrl = (environment?: ApplicationOriginEnvironment): URL =>
  new URL('/', resolveApplicationOrigins(environment).platform);

export const navigateToApplicationUrl = (destination: URL): void => {
  window.location.replace(destination.toString());
};
